import {
    LinkedInCampaignGroupUrn,
    LinkedInIABCategoryUrn,
    LinkedInIAuditStampActorUrn,
    LinkedInIOrganizationUrn,
    LinkedInPublisherFileUrn,
    LinkedInSponsoredAccountUrn,
} from './urn'

export type LinkedInCampaign = {
    storyDeliveryEnabled: boolean
    targetingCriteria: LinkedInTargetingCriteria
    connectedTelevisionOnly: boolean
    pacingStrategy: LinkedInPacingStrategy
    locale: {
        country: string
        language: string
    }
    type: LinkedInCampaignType
    runSchedule: {
        start: number
        end?: number
    }
    optimizationTargetType: LinkedInCampaignTargetType
    changeAuditStamps: {
        created: LinkedInChangeAuditStamp
        lastModified: LinkedInChangeAuditStamp
    }
    costType: LinkedInCostType
    creativeSelection: LinkedInCreativeSelectionType
    offsiteDeliveryEnabled: boolean
    id: number
    audienceExpansionEnabled: boolean
    test: boolean
    format: LinkedInCampaignFormat
    servingStatuses: LinkedInCampaignServingStatus[]
    version: {
        versionTag: string
    }
    objectiveType: LinkedInCampaignObjective
    associatedEntity: LinkedInIOrganizationUrn
    offsitePreferences: {
        iabCategories: {
            exclude: LinkedInIABCategoryUrn[]
        }
        publisherRestrictionFiles: {
            include: LinkedInPublisherFileUrn[]
            exclude: LinkedInPublisherFileUrn[]
        }
    }
    campaignGroup: LinkedInCampaignGroupUrn
    dailyBudget: LinkedInCost
    totalBudget: LinkedInCost
    unitCost: LinkedInCost
    name: string
    account: LinkedInSponsoredAccountUrn
    status: LinkedInCampaignStatus
    // Required on create — declares whether the campaign is political advertising.
    // https://learn.microsoft.com/en-us/linkedin/marketing/integrations/ads/account-structure/create-and-manage-campaigns
    politicalIntent: LinkedInPoliticalIntent
    // Frequency-cap config — currently only the BRAND_AWARENESS objective supports this. 202408+.
    optimizationPreference?: {
        frequencyOptimizationPreference?: LinkedInFrequencyOptimizationPreference
    }
}

export type LinkedInTimeSpan = {
    duration: number
    unit: 'DAY'
}

export type LinkedInFrequencyOptimizationPreference = {
    optimizationType: 'MAX_FREQUENCY'
    frequency: number
    timeSpan: LinkedInTimeSpan
}

export enum LinkedInPoliticalIntent {
    POLITICAL = 'POLITICAL',
    NOT_POLITICAL = 'NOT_POLITICAL',
    NOT_DECLARED = 'NOT_DECLARED',
}

export type LinkedInTargetingFacetType =
    | 'genders'
    | 'industries'
    | 'seniorities'
    | 'ageRanges'
    | 'profileLocations'
    | 'staffCountRanges'
    | 'employers'
    | 'employersPast'
    | 'employersAll'
    | 'groups'
    | 'titles'
    | 'titlesPast'
    | 'titlesAll'
    | 'skills'
    | 'schools'
    | 'followedCompanies'
    | 'degrees'
    | 'fieldsOfStudy'
    | 'yearsOfExperienceRanges'
    | 'firstDegreeConnectionCompanies'
    | 'audienceMatchingSegments'
    | 'dynamicSegments'
    | 'interests'
    | 'memberBehaviors'
    | 'companyCategory'
    | 'growthRate'
    | 'revenue'
    | 'locations'
    | 'audienceMatchingSegments'
    | 'jobFunctions'
    | 'interfaceLocales'
    | 'dynamicSegments'
    | 'audienceMatchingSegments'

export type LinkedInTargetingFacetUrn = `urn:li:adTargetingFacet:${LinkedInTargetingFacetType}`

export type LinkedInCost = {
    currencyCode: string
    amount: string
}

export enum LinkedInCampaignStatus {
    ACTIVE = 'ACTIVE',
    PAUSED = 'PAUSED',
    ARCHIVED = 'ARCHIVED',
    COMPLETED = 'COMPLETED',
    CANCELED = 'CANCELED',
    DRAFT = 'DRAFT',
    PENDING_DELETION = 'PENDING_DELETION',
    REMOVED = 'REMOVED',
}

export enum LinkedInCampaignObjective {
    WEBSITE_VISIT = 'WEBSITE_VISIT', // API returns with underscore, not hyphen. But documentation says hyphen!
    ENGAGEMENT = 'ENGAGEMENT',
    VIDEO_VIEW = 'VIDEO_VIEW',
    LEAD_GENERATION = 'LEAD_GENERATION',
    WEBSITE_CONVERSION = 'WEBSITE_CONVERSION',
    JOB_APPLICANT = 'JOB_APPLICANT',
    TALENT_LEAD = 'TALENT_LEAD',
    BRAND_AWARENESS = 'BRAND_AWARENESS',
    // legacies
    WEBSITE_TRAFFIC = 'WEBSITE_TRAFFIC',
    CREATIVE_ENGAGEMENT = 'CREATIVE_ENGAGEMENT',
}

export enum LinkedInCampaignServingStatus {
    RUNNABLE = 'RUNNABLE',
    STOPPED = 'STOPPED',
    BILLING_HOLD = 'BILLING_HOLD',
    ACCOUNT_TOTAL_BUDGET_HOLD = 'ACCOUNT_TOTAL_BUDGET_HOLD',
    ACCOUNT_END_DATE_HOLD = 'ACCOUNT_END_DATE_HOLD',
    ACCOUNT_SERVING_HOLD = 'ACCOUNT_SERVING_HOLD',
    CAMPAIGN_START_DATE_HOLD = 'CAMPAIGN_START_DATE_HOLD',
    CAMPAIGN_END_DATE_HOLD = 'CAMPAIGN_END_DATE_HOLD',
    CAMPAIGN_TOTAL_BUDGET_HOLD = 'CAMPAIGN_TOTAL_BUDGET_HOLD',
    CAMPAIGN_AUDIENCE_COUNT_HOLD = 'CAMPAIGN_AUDIENCE_COUNT_HOLD',
    CAMPAIGN_GROUP_START_DATE_HOLD = 'CAMPAIGN_GROUP_START_DATE_HOLD',
    CAMPAIGN_GROUP_END_DATE_HOLD = 'CAMPAIGN_GROUP_END_DATE_HOLD',
    CAMPAIGN_GROUP_TOTAL_BUDGET_HOLD = 'CAMPAIGN_GROUP_TOTAL_BUDGET_HOLD',
    CAMPAIGN_GROUP_STATUS_HOLD = 'CAMPAIGN_GROUP_STATUS_HOLD',
    RESTRICTED_HOLD = 'RESTRICTED_HOLD',
    INTERNAL_HOLD = 'INTERNAL_HOLD',
}

export enum LinkedInCampaignFormat {
    CAROUSEL = 'CAROUSEL',
    FOLLOW_COMPANY = 'FOLLOW_COMPANY',
    JOBS = 'JOBS',
    SINGLE_VIDEO = 'SINGLE_VIDEO',
    SPONSORED_INMAIL = 'SPONSORED_INMAIL',
    SPONSORED_MESSAGE = 'SPONSORED_MESSAGE',
    SPONSORED_UPDATE = 'SPONSORED_UPDATE',
    SPONSORED_UPDATE_EVENT = 'SPONSORED_UPDATE_EVENT',
    SPONSORED_UPDATE_JOB_POSTING = 'SPONSORED_UPDATE_JOB_POSTING',
    SPONSORED_UPDATE_NATIVE_DOCUMENT = 'SPONSORED_UPDATE_NATIVE_DOCUMENT',
    SPOTLIGHT = 'SPOTLIGHT',
    STANDARD_UPDATE = 'STANDARD_UPDATE',
    TEXT = 'TEXT',
    TEXT_AD = 'TEXT_AD',
    LEAD_GENERATION_FORM_SPONSORED_CONTENT = 'LEAD_GENERATION_FORM_SPONSORED_CONTENT',
    LEAD_GENERATION_FORM_CAROUSEL_SPONSORED_CONTENT = 'LEAD_GENERATION_FORM_CAROUSEL_SPONSORED_CONTENT',
    VIDEO_LEAD_GENERATION_FORM_SPONSORED_CONTENT = 'VIDEO_LEAD_GENERATION_FORM_SPONSORED_CONTENT',
    LEAD_GENERATION_FORM_SPONSORED_INMAIL = 'LEAD_GENERATION_FORM_SPONSORED_INMAIL',
    UNSUPPORTED = 'UNSUPPORTED',
    $UNKNOWN = '$UNKNOWN',
}

export type LinkedInChangeAuditStamp = {
    actor: LinkedInIAuditStampActorUrn
    time: number
}

export enum LinkedInCampaignLocationTargetingFacetType {
    LOCATIONS = 'LOCATIONS',
    PROFILE_LOCATIONS = 'PROFILE_LOCATIONS',
    NONE = 'NONE',
}

export enum LinkedInCampaignTargetType {
    NONE = 'NONE',
    ENHANCED_CONVERSION = 'ENHANCED_CONVERSION',
    // Auto-bidding — maximize a metric, no advertiser-specified bid.
    MAX_IMPRESSION = 'MAX_IMPRESSION',
    MAX_CLICK = 'MAX_CLICK',
    MAX_CONVERSION = 'MAX_CONVERSION',
    MAX_VIDEO_VIEW = 'MAX_VIDEO_VIEW',
    MAX_LEAD = 'MAX_LEAD',
    // Applicable only from API versions 202602 and later.
    MAX_QUALIFIED_LEAD = 'MAX_QUALIFIED_LEAD',
    MAX_REACH = 'MAX_REACH',
    // Target-cost bidding — maintain a specified average cost per event.
    TARGET_COST_PER_CLICK = 'TARGET_COST_PER_CLICK',
    TARGET_COST_PER_IMPRESSION = 'TARGET_COST_PER_IMPRESSION',
    TARGET_COST_PER_VIDEO_VIEW = 'TARGET_COST_PER_VIDEO_VIEW',
    // Cost-cap bidding — maximize a metric while keeping the average cost under a cap.
    CAP_COST_AND_MAXIMIZE_CLICKS = 'CAP_COST_AND_MAXIMIZE_CLICKS',
    CAP_COST_AND_MAXIMIZE_IMPRESSIONS = 'CAP_COST_AND_MAXIMIZE_IMPRESSIONS',
    CAP_COST_AND_MAXIMIZE_VIDEO_VIEWS = 'CAP_COST_AND_MAXIMIZE_VIDEO_VIEWS',
    CAP_COST_AND_MAXIMIZE_LEADS = 'CAP_COST_AND_MAXIMIZE_LEADS',
}

export enum LinkedInCampaignType {
    TEXT_AD = 'TEXT_AD',
    SPONSORED_UPDATES = 'SPONSORED_UPDATES',
    SPONSORED_INMAILS = 'SPONSORED_INMAILS',
    DYNAMIC = 'DYNAMIC',
    CONTENT = 'CONTENT',
}

export enum LinkedInPacingStrategy {
    LIFETIME = 'LIFETIME',
    // 202501+.
    ACCELERATED = 'ACCELERATED',
}

export enum LinkedInCostType {
    CPM = 'CPM',
    CPC = 'CPC',
    CPV = 'CPV',
}

export enum LinkedInCreativeSelectionType {
    ROUND_ROBIN = 'ROUND_ROBIN',
    OPTIMIZED = 'OPTIMIZED',
}

export type LinkedInTargetingCriteria = {
    include: {
        and: {
            or: Partial<Record<LinkedInTargetingFacetUrn, string[]>>
        }[]
    }
    exclude: {
        or: Partial<Record<LinkedInTargetingFacetUrn, string[]>>
    }
}

export type UpdateLinkedInCampaignRequest = {
    patch: {
        $set: Partial<LinkedInCampaign>
        // Field names to unset entirely — e.g. dropping totalBudget to revert to an unlimited budget.
        $delete?: (keyof LinkedInCampaign)[]
    }
}
